/**
 * 协作管理 · **纯逻辑层**（L 域端侧 · 零渲染、零请求）
 *
 * 依据：L 域 PRD **§5.1**（邀请与角色）· **§5.5**（加入家庭 · 4 栏预览 + 二选一策略）·
 * **§5.6**（移除 / 解散 = 分家不是删除）· `DR-018` · 契约 `contract/collaboration.ts`。
 *
 * ## 🔴 本层只做三件事
 *
 * | 做 | 为什么放在这里 |
 * |---|---|
 * | **枚举 → 人话**（角色 / 处置 / 策略） | 枚举单源在 `@jiaying/shared`，中文只在端侧（`collab-labels.ts`） |
 * | **绝对时刻 → 剩余天数** | `floorDiv` 是定点出口（ESLint 封了 `/` 与 `Math.round`）⇒ 需要一个能测的落点 |
 * | **服务端数组 → 渲染项**（4 栏 / 搬动清单） | 让 UI 里**没有一条业务判断**（铁律 34 的同型纪律） |
 *
 * ## ⚠️ 三件**刻意不做**的事（做了就是第二份实现）
 *
 * | 不做 | 为什么 |
 * |---|---|
 * | 🔴 **不比对**二次确认的名称（移除成员 / 解散账本） | 「必须与成员姓名 / 账本名完全相同」是**服务端规则**（`RemoveMemberRequestSchema` 的 400）。端侧只把用户输入**原样提交**，让服务端判 —— 端侧判一遍 = 规则两处各写一份（铁律 79） |
 * | 🔴 **不校验**邀请码形态（8 位 / 字符集） | 同上，`InviteCodeSchema` 在契约层；端侧只做"非空才可点"的**可用性**判断 |
 * | 🔴 **不判断**"成员是否已满 4 人" | 服务端 `POST /invites` 满员回 **409**（状态冲突，不是权限不足）⇒ 端侧原样展示 `message`，不自己数人头（人数会与服务端并发写入不一致） |
 */

import {
  floorDiv,
  JoinStrategy,
  LedgerRole,
  type InviteView,
  type JoinPreviewDiscard,
  type JoinPreviewView,
  type LedgerMemberView,
  type MovedCounts,
} from '@jiaying/shared';
import {
  DISCARD_DISPOSITION_TEXT,
  JOIN_STRATEGY_DISCARD,
  JOIN_STRATEGY_DISCARD_HINT,
  JOIN_STRATEGY_MERGE,
  JOIN_STRATEGY_MERGE_HINT,
} from './collab-labels.js';

const MS_PER_DAY = 86_400_000;

// ─────────────────────────── 角色 ───────────────────────────

/**
 * 能否看到协作**管理**动作（邀请 / 转让 / 移除 / 解散）。
 *
 * 🔴 判据只有 `role === owner` —— 与 `PermissionAction` 的七项协作动作一致
 * （`DR-018`：这七项**仅 owner**）。⚠️ 它只决定"**画不画入口**"，
 * **不**决定数据可见性（架构 §6.3：不得据角色隐藏数据）。
 */
export function canManageCollab(role: LedgerRole | string): boolean {
  return role === LedgerRole.Owner;
}

export function memberRoleText(role: LedgerRole | string): string {
  return role === LedgerRole.Owner ? '所有者' : '成员';
}

/** 成员展示名 —— `isSelf` 由服务端判（端侧不比对 id） */
export function memberDisplayName(m: LedgerMemberView): string {
  return m.isSelf ? `${m.name}（我）` : m.name;
}

// ─────────────────────────── 邀请码 ───────────────────────────

/**
 * 邀请码的展示形态：`ABCD EFGH`（4-4 分组，便于口头念）。
 *
 * 🔴 **只用于展示** —— 复制/提交一律用原始 `code`（分组后的字符串不是凭证）。
 * 非 8 位时**原样返回**（不猜、不截断：形态异常交给服务端判）。
 */
export function inviteCodeDisplay(code: string): string {
  const c = code.trim();
  if (c.length !== 8) return c;
  return `${c.slice(0, 4)} ${c.slice(4)}`;
}

/**
 * 剩余整日数（`<= 0` 即已过期）。
 *
 * 🔴 用 `floorDiv` 而不是 `/`：全项目禁浮点（`P域PRD` §3 / ESLint `no-restricted-syntax`）。
 * 向上取整到"还剩 N 天"是因为**不到一天也算一天**（用户心智：明天到期 = 还剩 1 天）。
 */
export function inviteDaysLeft(invite: Pick<InviteView, 'expiresAt'>, nowMs: number): number {
  const left = invite.expiresAt - nowMs;
  if (left <= 0) return 0;
  return floorDiv(left + MS_PER_DAY - 1, MS_PER_DAY);
}

export function inviteExpiryText(invite: Pick<InviteView, 'expiresAt'>, nowMs: number): string {
  const days = inviteDaysLeft(invite, nowMs);
  return days > 0 ? `还剩 ${String(days)} 天过期` : '已过期，请重新生成';
}

/** 邀请码是否还有效（决定是否提示"已过期"） */
export function inviteAlive(invite: Pick<InviteView, 'expiresAt'>, nowMs: number): boolean {
  return invite.expiresAt > nowMs;
}

// ─────────────────────────── 搬动数量（合并 / 拆分报告共用） ───────────────────────────

/**
 * `MovedCounts` → 逐项清单（**只列非零项**）。
 *
 * 🔴 **不求和**：契约里十二个字段各是不同的表（账户 / 流水 / 腿 / 目标…），
 * 加成一个"共 N 行"没有语义（就像不能把"账户数"和"腿数"相加）。
 * 依据 = `contract/collaboration.ts` 对 `MovedCounts` 的注释：「它是给用户看的，也是给测试断言的」。
 */
export function movedLines(moved: MovedCounts | null | undefined): { key: string; label: string; count: number }[] {
  if (moved === null || moved === undefined) return [];
  const pairs: readonly [keyof MovedCounts, string][] = [
    ['accounts', '账户'],
    ['transactions', '流水'],
    ['legs', '分录'],
    ['goals', '储蓄目标'],
    ['schedules', '周期规则'],
    ['budgets', '预算'],
    ['tags', '标签'],
    ['categories', '自建分类'],
    ['policies', '保单'],
    ['holdings', '投资持仓'],
    ['netWorthItems', '手动资产/负债'],
    ['majorAssets', '重大资产'],
  ];
  return pairs
    .filter(([k]) => Number(moved[k]) > 0)
    .map(([k, label]) => ({ key: String(k), label, count: Number(moved[k]) }));
}

/** 清单是否为空（"什么都没搬"要能说出来，而不是渲染一张空表） */
export function movedIsEmpty(moved: MovedCounts | null | undefined): boolean {
  return movedLines(moved).length === 0;
}

// ─────────────────────────── 加入家庭：4 栏预览 ───────────────────────────

export interface JoinPreviewColumn {
  /** ① 将并入的数量 */
  merge: { label: string; count: number }[];
  /** ② 撞名处理 */
  renames: { from: string; to: string }[];
  reuses: { kind: 'category' | 'tag'; text: string }[];
  /** ③ 不并入 */
  discarded: { name: string; count: number; disposition: string; note: string }[];
  /** ④ 不可撤销警示（原文下发，端侧不重写） */
  warning: string;
  /** 无源可并（`willMerge === null`）—— 此时**没有**策略可选 */
  noSource: boolean;
}

const REUSE_KIND_TEXT: Readonly<Record<'category' | 'tag', string>> = {
  category: '分类',
  tag: '标签',
};

/**
 * 把服务端预览整理成**可直接渲染的 4 栏**。
 *
 * 🔴 `disposition` 的中文映射只在 `collab-labels.ts`（三种性质分开说 ——
 * `discarded` / `unaffected` / `deferred` 的含义完全不同，合并成"丢弃"就是静默说谎）。
 */
export function joinPreviewColumn(p: JoinPreviewView): JoinPreviewColumn {
  const noSource = p.willMerge === null;
  return {
    merge: movedLines(p.willMerge).map((l) => ({ label: l.label, count: l.count })),
    renames: p.renames.map((r) => ({ from: r.from, to: r.to })),
    reuses: p.reuses.map((r) => ({
      kind: r.kind,
      text: `${REUSE_KIND_TEXT[r.kind]}「${r.name}」沿用家庭账本里的那一条`,
    })),
    discarded: p.discarded.map((d: JoinPreviewDiscard) => ({
      name: d.name,
      count: d.count,
      disposition: DISCARD_DISPOSITION_TEXT[d.disposition] ?? d.disposition,
      note: d.note,
    })),
    warning: p.irreversibleWarning,
    noSource,
  };
}

// ─────────────────────────── 加入家庭：策略 ───────────────────────────

export interface JoinStrategyOption {
  value: JoinStrategy;
  label: string;
  hint: string;
  /** 该策略会硬删源账本 ⇒ UI 需额外警示 */
  destructive: boolean;
}

/**
 * 可选的加入策略。
 *
 * 🔴 **无源可并（`willMerge === null`）⇒ 返回空数组**：此时契约里没有"合并/舍弃"
 * 之分，服务端也不该收到 `strategy`… ⚠️ 但 `JoinRequestSchema` 里 `strategy` 是**必填** ——
 * 所以此时 UI 仍要提交一个值（`merge`，语义上等价于"没有源可并"），
 * 而**不能**发明第三种取值（那会与契约分叉）。判据见 `joinStrategyToSubmit()`。
 */
export function joinStrategyOptions(willMergeIsNull: boolean): JoinStrategyOption[] {
  if (willMergeIsNull) return [];
  return [
    {
      value: JoinStrategy.Merge,
      label: JOIN_STRATEGY_MERGE,
      hint: JOIN_STRATEGY_MERGE_HINT,
      destructive: false,
    },
    {
      value: JoinStrategy.Discard,
      label: JOIN_STRATEGY_DISCARD,
      hint: JOIN_STRATEGY_DISCARD_HINT,
      destructive: true,
    },
  ];
}

/**
 * 提交时真正要填的 `strategy`。
 *
 * 🔴 无源可并时填 `merge`（不是"空"）：`merge` 在无源时的语义就是"没有东西要搬"，
 * 服务端 `join.service` 对 `willMerge === null` 走直通分支。⚠️ 若填 `discard`，
 * 一个"没有源账本"的用户会被当成"要求硬删源" —— 虽然此时无源可删，
 * 但这个取值会让日志与审计**说谎**。
 */
export function joinStrategyToSubmit(selected: JoinStrategy | null, willMergeIsNull: boolean): JoinStrategy {
  if (willMergeIsNull) return JoinStrategy.Merge;
  return selected ?? JoinStrategy.Merge;
}

/** 是否必须先选策略才能提交（有源时必须显式选一次 —— `AC-L5.4` 不可跳过） */
export function needsExplicitStrategy(willMergeIsNull: boolean, selected: JoinStrategy | null): boolean {
  if (willMergeIsNull) return false;
  return selected === null;
}

// ─────────────────────────── 二次确认 ───────────────────────────

/**
 * 二次确认对话框的"提交"是否可点。
 *
 * 🔴 **只判非空**（可用性），**不判是否与目标名称相同** —— 那条规则在服务端
 * （`RemoveMemberRequestSchema` / `DissolveLedgerRequestSchema` 的 400）。
 * 端侧再判一遍就是同一规则两处实现，且服务端改措辞后端侧会静默不跟（铁律 79）。
 */
export function confirmSubmittable(input: string, busy: boolean): boolean {
  return !busy && input.trim().length > 0;
}
